'use client';
import React, { useState } from 'react';
import { ClipboardPaste, ChevronDown, ChevronUp } from 'lucide-react';

interface PasteDetectorProps {
  onParsedItems: (items: string[]) => void;
}

export default function PasteDetector({ onParsedItems }: PasteDetectorProps) {
  const [expanded, setExpanded] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parsed, setParsed] = useState<string[]>([]);

  const handleParse = () => {
    const lines = pasteText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    setParsed(lines);
  };

  const handleAddAll = () => {
    onParsedItems(parsed);
    setPasteText('');
    setParsed([]);
    setExpanded(false);
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-3 bg-secondary/30 hover:bg-secondary/50 transition-colors text-sm font-medium text-foreground"
      >
        <span className="flex items-center gap-2">
          <ClipboardPaste size={15} className="text-primary" />
          Paste Multiple Items at Once
        </span>
        {expanded ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="p-4 space-y-3 fade-in">
          <p className="text-xs text-muted-foreground">
            Paste a list of items — one per line. Each line will be added as a separate item with the room and family member selected in the form.
          </p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"65-inch Samsung TV\nDyson vacuum cleaner\nKitchenAid stand mixer\nSonos soundbar"}
            rows={5}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground font-mono"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleParse}
              disabled={!pasteText.trim()}
              className="px-3 py-2 text-xs font-medium border border-border rounded-lg bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
            >
              Preview Items
            </button>
            {parsed.length > 0 && (
              <button
                type="button"
                onClick={handleAddAll}
                className="px-3 py-2 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 transition-all duration-150 active:scale-95"
              >
                Add {parsed.length} Items to Inventory
              </button>
            )}
          </div>

          {parsed.length > 0 && (
            <div className="bg-secondary/30 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-foreground mb-2">{parsed.length} items detected:</p>
              {parsed.map((item, i) => (
                <div key={`parsed-${i}-${item.slice(0, 8)}`} className="flex items-center gap-2 text-xs text-foreground">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </span>
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}